<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import {
  getCookie, postReviewAPI, postMessage, actionPermission, saveTokenToCookie, checkActionPermission,
} from './methods';
import StepZero from './components/StepZero.vue';
import StepOne from './components/StepOne.vue';
import StepTwo from './components/StepTwo.vue';
import StepThree from './components/StepThree.vue';
import Toasts from './components/Toasts.vue';

const videoCourseId = ref(''); // 課程 ID
const data = ref({
  methodType: 'post',
  originReviewData: {
    rating: null,
    content: null,
  },
  reviewData: {
    rating: null,
    content: null,
  },
});
const step = ref(0);
const stepChange = (param) => {
  step.value = param;
};
const teachableToken = ref({
  token: '',
  expiration: '',
  isLink: false,
});

const reviewModal = ref(false);
const openModal = () => {
  reviewModal.value = true;
  checkActionPermission();
  // 如果是已經綁定過的 rpg token 跳過這段
  const tokenType = getCookie('rpg_temp_token_type');
  if (tokenType !== 'rpg') {
    if (!teachableToken.value.isLink) { // 未綁定過，直接使用 teachable token
      saveTokenToCookie(teachableToken.value.expiration, teachableToken.value.token, 'teachable', 'false');
    } else {
      saveTokenToCookie(teachableToken.value.expiration, teachableToken.value.token, 'teachable', 'true');
    }
  }
  if (actionPermission.value) {
    step.value = 1;
  } else {
    step.value = 0;
  }
};
const closeModal = () => {
  reviewModal.value = false;
  // 把課程 id 紀錄起來代表自動跳出過 Modal 一次惹
  const reviewModalStatus = JSON.parse(window.localStorage.getItem('h_b135a9_review_51cb8_modal'))
    || [];
  const arr = [...reviewModalStatus];
  if (!reviewModalStatus.includes(videoCourseId.value)) {
    arr.push(videoCourseId.value);
  }
  window.localStorage.setItem(
    'h_b135a9_review_51cb8_modal',
    JSON.stringify(arr),
  );
  step.value = 1;
};

const getReviewData = async () => {
  // 先從 localStorage 確認是否有暫存
  // storeReview 是暫存 rating、content，避免重複 call API
  // reviewModalStatus：用 courseId 判斷是否有彈出過 Modal（open） 了
  const storeReview = window.localStorage.getItem('h_b135a9_review_51cb8');
  const storeReviewModalJump = window.localStorage.getItem(
    'h_b135a9_review_51cb8_modal',
  );
  if (storeReviewModalJump === '') {
    localStorage.removeItem('h_b135a9_review_51cb8_modal');
  }
  const reviewModalStatus = JSON.parse(window.localStorage.getItem('h_b135a9_review_51cb8_modal'))
    || [];
  if (storeReview && JSON.parse(storeReview).courseId === videoCourseId.value) {
    const storageReview = JSON.parse(storeReview);
    data.value.originReviewData = JSON.parse(JSON.stringify({
      can_review: true,
      rating: storageReview.rating,
      content: storageReview.content,
    }));
    data.value.reviewData = {
      can_review: true,
      rating: storageReview.rating,
      content: storageReview.content,
    };
  } else {
    const res = await axios.get(
      `/v1/video-courses/${videoCourseId.value}/review`,
    );
    data.value.originReviewData = JSON.parse(JSON.stringify(res.data));
    data.value.reviewData = res.data;
    if (data.value.reviewData.rating && data.value.reviewData.content) {
      window.localStorage.setItem(
        'h_b135a9_review_51cb8',
        JSON.stringify({
          courseId: videoCourseId.value,
          rating: data.value.reviewData.rating,
          content: data.value.reviewData.content,
          date: new Date().getTime(),
        }),
      );
    }
    // modal 彈出條件：可以填寫時，尚未未填寫過，且沒有關閉 modal
    if (data.value.reviewData.can_review) {
      if (
        !data.value.reviewData.rating
        && !reviewModalStatus.includes(videoCourseId.value)
      ) {
        openModal();
      }
    }
  }
  if (data.value.reviewData.rating !== null) {
    data.value.methodType = 'put';
  }
};
const changeData = (review) => {
  data.value.reviewData.rating = Math.floor(review.rating);
  data.value.reviewData.content = review.content;
};
const changeQData = (questionnaires) => {
  data.value.questionnairesData = questionnaires;
};
// 送出回覆
const postReviewData = async () => {
  const obj = {
    rating: data.value.reviewData.rating,
    content: data.value.methodType === 'post' && data.value.reviewData.content === '' ? null : data.value.reviewData.content,
  };
  try {
    await postReviewAPI(
      data.value.methodType,
      videoCourseId.value,
      obj,
    );
    step.value += 1;
  } catch (err) {
    step.value -= 1;
    postMessage('danger', '評論回覆出現錯誤', `${err?.response?.data?.message}`);
  }
};

const user = ref({
  teachable_id: '',
  email: '',
});
const login = () => {
  axios
    .post(
      `${
        process.env.NODE_ENV === 'production'
          ? process.env.VUE_APP_API_USER_SYSTEM_URL
          : ''
      }/v1/user/login/teachable`,
      {
        teachable_id: user.value.teachable_id || '',
        email: user.value.email || '',
      },
    )
    .then((res) => {
      teachableToken.value.token = res.data.access_token;
      teachableToken.value.expiration = res.data.expiration;
      teachableToken.value.isLink = res.data.is_link;
      axios.defaults.headers.common.Authorization = `Bearer ${res.data.access_token}`;
      getReviewData();
    });
};
onMounted(() => {
  const userInfo = document.querySelector('#fedora-data');
  if (process.env.NODE_ENV === 'production') {
    videoCourseId.value = document
      .getElementById('video-course-id').textContent;
  } else {
    // eslint-disable-next-line prefer-destructuring
    videoCourseId.value = document.getElementById('lecture_heading').dataset.lectureUrl.split('/')[2];
  }
  if (userInfo) {
    user.value.teachable_id = userInfo.dataset.id;
    user.value.email = userInfo.dataset.email;
    const tokenInCookie = getCookie('rpg_temp_token');
    const tokenType = getCookie('rpg_temp_token_type');
    const isLinkStatus = getCookie('rpg_temp_token_is_link') === 'true';
    // 要有 rpg token 才能發問
    if (tokenType === 'rpg' && isLinkStatus && tokenInCookie && tokenInCookie !== '') {
      axios.defaults.headers.common.Authorization = `Bearer ${tokenInCookie}`;
      getReviewData();
    } else {
      login();
    }
  }
});

// const addSec = async () => {
//   const res = await axios.post(
//     `/v1/video-courses/${videoCourseId.value}/read-sec`,
//     {
//       read_sec: 100,
//     }
//   );
//   console.log(res);
// };
</script>

<template>
  <!-- <button
    type="button"
    class="btn btn-hex-review-primary border-0 d-flex align-items-center"
    @click="addSec"
  >
    + 100 秒
  </button> -->
  <Toasts />
  <!-- <div class="bg-hex-review-dark p-2" style="min-height: 40px">
    <button
      v-if="data.reviewData.can_review"
      type="button"
      class="btn btn-outline-light border-0 d-flex align-items-center"
      @click="openModal"
    >
      <span class="material-icons-outlined me-1"> grade </span>留下評價
    </button>
  </div> -->
  <button
    v-if="data.reviewData.can_review"
    type="button"
    class="btn btn-outline-light border-0 d-flex align-items-center me-2"
    @click="openModal"
  >
    <span class="material-icons-outlined me-1"> grade </span>留下評價
  </button>
  <div
    v-if="reviewModal"
    class="review-modal"
    id="reviewModal"
    tabindex="-1"
    aria-labelledby="exampleModalLabel"
    aria-hidden="true"
  >
    <div class="review-modal-dialog">
      <div class="modal-content">
        <a
          v-if="step === 2"
          href="#"
          @click.prevent="stepChange(1)"
          class="link-hex-review-primary"
          style="position: absolute; left: 15px; top: 15px; z-index: 1101"
        >
          返回
        </a>
        <a
          href="#"
          @click.prevent="closeModal"
          class="link-hex-review-dark"
          style="position: absolute; right: 15px; top: 15px; z-index: 1101"
        >
          <span class="material-icons-outlined"> close </span>
        </a>
        <StepZero v-if="step === 0" />
        <StepOne
          v-if="step === 1"
          @change-step="stepChange"
          :review-data="data.reviewData"
          @change-data="changeData"
        />
        <StepTwo
          v-if="step === 2"
          @submit="postReviewData"
          @get-data="getReviewData"
          @change-step="stepChange"
          :origin-review-data="data.originReviewData"
          :review-data="data.reviewData"
          :review-method="data.methodType"
          @change-data="changeQData"
        />
        <StepThree
          v-if="step === 3"
          @change-step="stepChange"
          :review-data="data.reviewData"
          @close="closeModal"
        />
      </div>
    </div>
  </div>
  <div v-if="reviewModal" class="modal-backdrop fade show"></div>
</template>

<style lang="scss">
@import "./assets/scss/all.scss";
.review-modal-bg {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10000;
  min-height: 550px;
  background-color: rgba(0, 0, 0, 0.4);
}
.review-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  display: block;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}
@media (min-width: 576px) {
  .review-modal-dialog {
    max-width: 636px;
    margin: 1.75rem auto;
  }
}
.review-modal-dialog {
    position: relative;
    width: auto;
    margin: 0.5rem auto;
    pointer-events: none;
}
</style>, getCookieimport { values } from 'core-js/core/array';
